/**
 * IKService - Pure business logic for inverse kinematics calculations
 * 
 * This service contains IK calculations without React dependencies.
 * It can be tested in isolation and used in different contexts.
 */

import { createKinematicsSolver } from '../kinematics/URKinematics.js';

// Import types for JSDoc
// eslint-disable-next-line no-unused-vars
import { Types } from '../types/index.js';

/**
 * Create an IK solver for a robot type
 * @param {string} robotType - Robot type (e.g., 'ur10e', 'UR20')
 * @returns {{solver: Object, error: string|null}} Solver instance or error
 */
export function createIKSolver(robotType) {
  try {
    // Convert robot type string to match kinematics model names
    const robotModel = robotType.charAt(0).toUpperCase() +
                       robotType.charAt(1).toUpperCase() +
                       robotType.slice(2).toLowerCase();
    const solver = createKinematicsSolver(robotModel);
    return { solver, error: null };
  } catch (error) {
    return { solver: null, error: error.message };
  }
}

/**
 * Compute IK solution for a target pose
 * @param {Object} solver - IK solver instance
 * @param {Types.Pose} targetPose - Target position and rotation
 * @param {Array<number>|null} initialJoints - Initial joint positions for optimization
 * @returns {{solution: Array<number>|null, error: string|null}}
 */
export function computeIKSolution(solver, targetPose, initialJoints = null) {
  try {
    const solution = solver.computeIK(targetPose, initialJoints);
    if (!solution) {
      return { solution: null, error: 'No IK solution found' };
    }
    return { solution, error: null };
  } catch (error) {
    return { solution: null, error: error.message };
  }
}

/**
 * Compute all valid IK solutions for a target pose
 * @param {Object} solver - IK solver instance
 * @param {Pose} targetPose - Target position and rotation
 * @param {Array<number>|null} currentJoints - Current joint positions (for sorting)
 * @returns {Array<Array<number>>} Array of valid joint configurations
 */
export function computeAllIKSolutions(solver, targetPose, currentJoints = null) {
  if (!solver) return [];
  
  // Get all solutions using null for consistent results
  const allSolutions = solver.computeIKAll(targetPose, null);
  
  // Get robot parameters for joint limit filtering
  const robotParams = solver.getRobotInfo();
  
  // Filter for valid solutions within joint limits
  const validSolutions = allSolutions.filter(sol => {
    for (let i = 0; i < sol.length; i++) {
      const angle = sol[i];
      const normalized = ((angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
      const adjustedAngle = normalized > Math.PI ? normalized - 2 * Math.PI : normalized;
      
      if (adjustedAngle < robotParams.jointLimits[i].min || 
          adjustedAngle > robotParams.jointLimits[i].max) {
        return false;
      }
    }
    return true;
  });
  
  // Sort by distance to current joints if available
  if (currentJoints) {
    validSolutions.sort((a, b) => {
      const distA = a.reduce((sum, angle, i) => sum + Math.pow(angle - currentJoints[i], 2), 0);
      const distB = b.reduce((sum, angle, i) => sum + Math.pow(angle - currentJoints[i], 2), 0);
      return distA - distB;
    });
  }
  
  return validSolutions;
}

/**
 * Verify if a joint configuration produces the target pose
 * @param {Object} solver - IK solver instance
 * @param {Array<number>} jointPositions - Joint configuration to verify
 * @param {Pose} targetPose - Expected target pose
 * @returns {Object|null} Verification result with position/rotation errors
 */
export function verifyIKSolution(solver, jointPositions, targetPose) {
  if (!solver) return null;
  return solver.verifyIKSolution(jointPositions, targetPose);
}

/**
 * Select the best IK solution based on criteria
 * @param {Array<Array<number>>} solutions - Available IK solutions
 * @param {Array<number>|null} currentJoints - Current joint positions
 * @param {string} criteria - Selection criteria ('nearest', 'first', etc.)
 * @returns {Array<number>|null} Selected joint configuration
 */
export function selectBestSolution(solutions, currentJoints = null, criteria = 'nearest') {
  if (!solutions || solutions.length === 0) {
    return null;
  }
  
  switch (criteria) {
    case 'first':
      return solutions[0];
      
    case 'nearest':
      if (!currentJoints) {
        return solutions[0];
      }
      
      let bestSolution = solutions[0];
      let minDistance = Infinity;
      
      for (const sol of solutions) {
        const distance = sol.reduce((sum, angle, i) => 
          sum + Math.pow(angle - currentJoints[i], 2), 0
        );
        if (distance < minDistance) {
          minDistance = distance;
          bestSolution = sol;
        }
      }
      return bestSolution;
      
    default:
      return solutions[0];
  }
}

/**
 * Calculate joint motion required between two configurations
 * @param {Array<number>} fromJoints - Starting joint configuration
 * @param {Array<number>} toJoints - Target joint configuration
 * @returns {{totalMotion: number, maxJointMotion: number, jointMotions: Array<number>}}
 */
export function calculateJointMotion(fromJoints, toJoints) {
  const jointMotions = toJoints.map((to, i) => Math.abs(to - fromJoints[i]));
  
  return {
    totalMotion: jointMotions.reduce((sum, m) => sum + m, 0),
    maxJointMotion: Math.max(...jointMotions),
    jointMotions
  };
}

/**
 * Check if a joint configuration is within limits
 * @param {Array<number>} joints - Joint configuration
 * @param {Array<JointLimits>} limits - Joint limits
 * @returns {{valid: boolean, violations: Array<{joint: number, value: number, limit: JointLimits}>}}
 */
export function checkJointLimits(joints, limits) {
  const violations = [];
  
  for (let i = 0; i < joints.length; i++) {
    const angle = joints[i];
    // Normalize to [-π, π]
    const normalized = ((angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    const adjustedAngle = normalized > Math.PI ? normalized - 2 * Math.PI : normalized;
    
    if (adjustedAngle < limits[i].min || adjustedAngle > limits[i].max) {
      violations.push({
        joint: i,
        value: adjustedAngle,
        limit: limits[i]
      });
    }
  }
  
  return {
    valid: violations.length === 0,
    violations
  };
}
